import React from 'react';
import { Users, Luggage, Check, XCircle, Gift } from 'lucide-react';
import { formatMoney } from '../../utils/formatMoney';

const VehicleDetailCard = ({ vehicle, isSelected, onSelect, passengerCount, luggageCount }) => {
  const actualLuggage = luggageCount || passengerCount;
  
  // Cloudinary vehicle images
  const defaultImages = {
    VAN: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/van_zxi1eg.png',
    MINI: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/mini_r98z8m.png',
    MIDI: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334447/midi_pgk2do.png',
    BUS: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334447/bus_esh2br.png',
    MINIVAN: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/van_zxi1eg.png',
    MINIBUS: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/mini_r98z8m.png',
    MIDIBUS: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334447/midi_pgk2do.png',
  };
  
  // Get image based on vehicle name/category
  const getVehicleImage = () => {
    if (vehicle.image && !vehicle.image.includes('zupp.in')) {
      return vehicle.image;
    }
    const category = (vehicle.category || vehicle.id || '').toUpperCase();
    const name = (vehicle.name || '').toUpperCase();
    
    if (name.includes('MINIVAN') || category === 'VAN') return defaultImages.VAN;
    if (name.includes('MINIBUS') || category === 'MINI') return defaultImages.MINI;
    if (name.includes('MIDIBUS') || category === 'MIDI') return defaultImages.MIDI;
    if (name.includes('BUS') || category === 'BUS') return defaultImages.BUS;
    
    return defaultImages[category] || defaultImages.VAN;
  };
  
  const vehicleImage = getVehicleImage();
  
  return (
    <div
      className={`bg-white rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${
        isSelected
          ? 'ring-2 ring-[#FF8C00] shadow-lg'
          : 'shadow-sm hover:-translate-y-0.5 hover:shadow-md'
      }`}
      onClick={() => onSelect(vehicle)}
      data-testid={`vehicle-card-${vehicle.id.toLowerCase()}`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Vehicle Image */}
        <div className="md:w-64 h-48 md:h-auto bg-gray-50 flex items-center justify-center p-6 relative">
          <img
            src={vehicleImage}
            alt={vehicle.name}
            className="max-h-32 md:max-h-40 w-auto object-contain"
            onError={(e) => {
              e.target.src = defaultImages.VAN;
            }}
          />
          {/* Selected badge - non-clickable */}
          {isSelected && (
            <div className="absolute top-3 right-3 bg-[#FF8C00] text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
              <Check className="h-3.5 w-3.5" />
              Selected
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 flex flex-col">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-xl font-bold text-black font-display">
              {vehicle.name}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {vehicle.description}
            </p>
          </div>

          {/* Capacity */}
          <div className="flex items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#FF8C00]/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-[#FF8C00]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Passengers</p>
                <p className="text-sm font-semibold text-black">
                  <span className="text-[#FF8C00]">{passengerCount}</span>/{vehicle.maxPassengers}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#FF8C00]/10 flex items-center justify-center">
                <Luggage className="h-4 w-4 text-[#FF8C00]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Luggage</p>
                <p className="text-sm font-semibold text-black">
                  <span className="text-[#FF8C00]">{actualLuggage}</span>/{vehicle.maxLuggage}
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-2 mb-4">
            {/* Free Cancellation & Meet & Greet badges */}
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              Free Cancel
            </span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium flex items-center gap-1">
              <Gift className="h-3 w-3" />
              Free M&G
            </span>
            {vehicle.features?.slice(0, 2).map((feature, index) => (
              <span
                key={index}
                className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
              >
                {feature}
              </span>
            ))}
          </div>

          {/* Price - ALWAYS 2 decimals, NO rounding */}
          <div className="mt-auto flex items-end justify-between pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 mb-1">Fixed price</p>
              <p className="text-2xl font-bold text-black font-display">
                {formatMoney(vehicle.price, vehicle.currency || 'EUR')}
              </p>
              <p className="text-xs text-[#FF8C00] font-medium">No hidden fees</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailCard;
